import React, { useMemo, useState } from "react";
import { THEME, flexxState, awardXP } from "./state";
import { DECKS, ALL_CARDS, CARD_BY_ID, SR_INTERVALS_DAYS, DAY_MS, dueAtFor } from "./flashcardData";
import Portal from "./Portal";

// Returns array of card objects (augmented) that are due now or overdue, sorted by dueAt ascending
function getDueCards(progress) {
  const now = Date.now();
  return ALL_CARDS
    .map((c) => ({ card: c, p: progress[c.id] }))
    .filter(({ p }) => p && !p.mastered && p.dueAt <= now)
    .sort((a, b) => a.p.dueAt - b.p.dueAt)
    .map(({ card, p }) => ({ ...card, progress: p }));
}

function countOverdue(progress) {
  const now = Date.now();
  return Object.values(progress).filter((p) => !p.mastered && p.dueAt < now - DAY_MS).length;
}

export function useFlashcardStats() {
  const progress = flexxState.flashcards();
  const due = getDueCards(progress);
  const overdue = countOverdue(progress);
  const total = ALL_CARDS.length;
  const seeded = Object.keys(progress).length;
  const mastered = Object.values(progress).filter((p) => p.mastered).length;
  return { progress, due, overdue, total, seeded, mastered };
}

// Dashboard panel — list of decks + due badge + start review
export function FlashcardsPanel() {
  const [, setTick] = useState(0);
  const [review, setReview] = useState(null);
  const stats = useFlashcardStats();

  const startReviewAll = () => {
    // If no due cards AND user hasn't seeded any, immediately seed all
    if (stats.due.length === 0 && stats.seeded === 0) {
      flexxState.seedFlashcardsImmediate(ALL_CARDS);
      setTick((t) => t + 1);
      setReview({ deckId: null });
      return;
    }
    setReview({ deckId: null });
  };
  const startDeck = (deckId) => {
    const deck = DECKS.find((d) => d.id === deckId);
    flexxState.seedFlashcardsImmediate(deck.cards);
    setTick((t) => t + 1);
    setReview({ deckId });
  };

  const dueCount = stats.due.length;
  const badgeClass = dueCount === 0 ? "green" : stats.overdue > 0 ? "" : "gold";

  return (
    <div data-testid="flexx-flashcards-panel" className="flexx-fade-in">
      <div className="flexx-card" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>REVIEW QUEUE</div>
            <div style={{ color: THEME.text, fontSize: 20, fontWeight: 700, marginTop: 4 }}>
              {dueCount === 0 ? "All caught up" : `${dueCount} card${dueCount === 1 ? "" : "s"} due today`}
              <span className={`flexx-badge-dot ${badgeClass}`} data-testid="flashcards-due-badge">{dueCount}</span>
            </div>
            <div style={{ color: THEME.mute, fontSize: 12, marginTop: 4 }}>
              Mastered: <b style={{ color: THEME.green }}>{stats.mastered}</b> / {stats.total} · Intervals: {SR_INTERVALS_DAYS.join("d · ")}d
            </div>
          </div>
          <button
            data-testid="flashcards-start-review-btn"
            className="flexx-btn-primary"
            onClick={startReviewAll}
            disabled={stats.seeded > 0 && dueCount === 0}
          >
            {stats.seeded === 0 ? "Start First Review" : dueCount > 0 ? "Start Review Session →" : "Nothing Due"}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 12 }}>
        {DECKS.map((d) => {
          const deckProgress = Object.entries(stats.progress)
            .filter(([cid]) => CARD_BY_ID[cid]?.deckId === d.id);
          const seeded = deckProgress.length;
          const mastered = deckProgress.filter(([, p]) => p.mastered).length;
          const due = deckProgress.filter(([, p]) => !p.mastered && p.dueAt <= Date.now()).length;

          return (
            <div key={d.id} className="flexx-card" data-testid={`flashcard-deck-${d.id}`} style={{ borderColor: `${d.color}60` }}>
              <div style={{ fontSize: 10, letterSpacing: 2, color: d.color, fontFamily: "monospace", marginBottom: 6 }}>DECK</div>
              <div style={{ color: THEME.text, fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{d.name}</div>
              <div style={{ color: THEME.mute, fontSize: 12, marginBottom: 12 }}>
                {d.cards.length} cards · {mastered} mastered · {due} due
              </div>
              <button
                data-testid={`flashcard-deck-start-${d.id}`}
                className="flexx-btn-ghost"
                style={{ width: "100%", fontSize: 12 }}
                onClick={() => startDeck(d.id)}
              >
                Review Deck
              </button>
            </div>
          );
        })}
      </div>

      {review && (
        <FlashcardReviewModal
          deckId={review.deckId}
          onClose={() => { setReview(null); setTick((t) => t + 1); }}
        />
      )}
    </div>
  );
}

function FlashcardReviewModal({ deckId, onClose }) {
  const [progress] = useState(() => flexxState.flashcards());
  const queue = useMemo(() => {
    const due = getDueCards(flexxState.flashcards());
    if (deckId) return due.filter((c) => c.deckId === deckId);
    return due;
  }, [deckId]);

  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [completed, setCompleted] = useState(0);
  const [finished, setFinished] = useState(queue.length === 0);
  const touchStart = React.useRef(null);

  if (finished) {
    return (
      <Portal>
      <div data-testid="flashcard-review-complete" style={overlay}>
        <div className="flexx-slide-up" style={container}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>REVIEW COMPLETE</div>
          <h2 style={{ color: THEME.text, fontSize: 22, margin: "6px 0 10px" }}>
            {queue.length === 0 ? "No cards due right now" : `${completed} card${completed === 1 ? "" : "s"} reviewed`}
          </h2>
          <p style={{ color: THEME.mute, fontSize: 13, lineHeight: 1.6 }}>
            Spaced repetition compounds. Come back tomorrow — your brain literally rewires itself between sessions.
          </p>
          <button data-testid="flashcard-review-close-btn" className="flexx-btn-primary" style={{ width: "100%", marginTop: 14 }} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
      </Portal>
    );
  }

  const card = queue[idx];

  const handle = (gotIt) => {
    const cur = flexxState.flashcards()[card.id] || card.progress || { intervalIdx: 0, reviews: 0 };
    let next;
    if (gotIt) {
      const nextIdx = cur.intervalIdx + 1;
      const mastered = nextIdx >= SR_INTERVALS_DAYS.length;
      next = {
        ...cur,
        intervalIdx: Math.min(nextIdx, SR_INTERVALS_DAYS.length - 1),
        dueAt: mastered ? Date.now() + 365 * 86400000 : dueAtFor(nextIdx),
        reviews: (cur.reviews || 0) + 1,
        mastered,
      };
    } else {
      next = { ...cur, dueAt: Date.now() + 86400000, reviews: (cur.reviews || 0) + 1, mastered: false };
    }
    flexxState.saveFlashcard(card.id, next);
    setCompleted((c) => c + 1);
    setFlipped(false);
    if (idx + 1 >= queue.length) {
      // End of session
      awardXP(30, "Flashcard review");
      setFinished(true);
    } else {
      setIdx(idx + 1);
    }
  };

  // Swipe support
  const onTouchStart = (e) => { touchStart.current = e.changedTouches[0].clientX; };
  const onTouchEnd = (e) => {
    const sx = touchStart.current;
    if (sx == null) return;
    const dx = e.changedTouches[0].clientX - sx;
    if (Math.abs(dx) < 60) return;
    if (dx > 0) handle(true);
    else handle(false);
    touchStart.current = null;
  };

  const progressPct = Math.round(((idx + (flipped ? 0.5 : 0)) / queue.length) * 100);

  return (
    <Portal>
    <div data-testid="flashcard-review-modal" style={overlay}>
      <div className="flexx-slide-up" style={{ ...container, maxWidth: 540 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>FLASHCARD REVIEW</div>
            <div style={{ color: THEME.text, fontSize: 14, fontWeight: 600, marginTop: 4 }}>{card.deckName}</div>
          </div>
          <button aria-label="Close" data-testid="flashcard-review-x" onClick={onClose} style={{ background: "transparent", border: "none", color: THEME.mute, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div className="flexx-progress-bar" style={{ marginBottom: 16 }}>
          <span style={{ width: `${progressPct}%` }} />
        </div>
        <div style={{ color: THEME.mute, fontSize: 11, marginBottom: 12, textAlign: "center" }}>
          {completed + 1} of {queue.length}
        </div>

        <div
          className="flexx-flip-scene"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div
            className={`flexx-flip-card ${flipped ? "flipped" : ""}`}
            data-testid="flashcard-flip"
            onClick={() => setFlipped((f) => !f)}
          >
            <div className="flexx-flip-face">
              <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 10 }}>QUESTION</div>
              <div style={{ color: THEME.text, fontSize: 18, lineHeight: 1.5 }}>{card.q}</div>
              <div style={{ color: THEME.mute, fontSize: 11, marginTop: 14 }}>Tap to flip</div>
            </div>
            <div className="flexx-flip-face back">
              <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 10 }}>ANSWER</div>
              <div style={{ color: THEME.text, fontSize: 15, lineHeight: 1.6 }}>{card.a}</div>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
          <button
            data-testid="flashcard-review-again-btn"
            className="flexx-btn-ghost"
            style={{ flex: 1 }}
            onClick={() => handle(false)}
          >
            ↺ Review Again
          </button>
          <button
            data-testid="flashcard-got-it-btn"
            className="flexx-btn-primary"
            style={{ flex: 1 }}
            onClick={() => handle(true)}
          >
            ✓ Got it
          </button>
        </div>
        <div style={{ fontSize: 11, color: THEME.mute, marginTop: 10, textAlign: "center" }}>
          Swipe right = Got it · Swipe left = Review again
        </div>
      </div>
    </div>
    </Portal>
  );
}

const overlay = {
  position: "fixed", inset: 0, zIndex: 9800,
  background: "rgba(0,0,0,0.88)",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
};
const container = {
  background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 18, padding: 24,
  maxWidth: 520, width: "100%", maxHeight: "92vh", overflowY: "auto",
};
